import React, { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { 
  Camera, Upload, X, Play, Loader2, CheckCircle, AlertTriangle,
  Target, Zap, Shield, Heart, TrendingUp, RotateCcw, Eye,
  ChevronDown, ChevronUp, Video, Sparkles, Award, Dumbbell, Crown
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

// Floating Analyze Button Component
export const FloatingAnalyzeButton = ({ onClick }) => {
  const [isHovered, setIsHovered] = useState(false);

  return (
    <motion.button
      onClick={onClick}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white font-bold rounded-full shadow-lg hover:shadow-xl transition-shadow"
      whileHover={{ scale: 1.05 }}
      whileTap={{ scale: 0.95 }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      data-testid="floating-analyze-btn"
    >
      <Sparkles className="w-5 h-5" />
      <AnimatePresence>
        {isHovered && (
          <motion.span
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: "auto", opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            className="overflow-hidden whitespace-nowrap"
          >
            AI Analyze
          </motion.span>
        )}
      </AnimatePresence>
    </motion.button>
  );
};

// Main Drill Analyzer Component
const DrillAnalyzer = ({ isOpen, onClose, drillContext = "" }) => {
  const [step, setStep] = useState("upload"); // upload, processing, results, upgrade
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState(null);
  const [drillName, setDrillName] = useState(drillContext);
  const [focusAreas, setFocusAreas] = useState("");
  const [analysisId, setAnalysisId] = useState(null);
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showDetailedScores, setShowDetailedScores] = useState(false);
  const [featureAccess, setFeatureAccess] = useState(null);
  const [loadingAccess, setLoadingAccess] = useState(true);
  
  const fileInputRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // Check feature access on mount
  useEffect(() => {
    if (isOpen) {
      checkFeatureAccess();
    }
    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [isOpen]);

  const checkFeatureAccess = async () => {
    setLoadingAccess(true);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${API}/api/subscription/feature-access`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setFeatureAccess(response.data);
      
      // If no access, show upgrade prompt
      if (response.data.ai_analyses_remaining === 0 && response.data.ai_analyses_limit !== -1) {
        setStep("upgrade");
      }
    } catch (err) {
      console.error("Error checking feature access:", err);
      setFeatureAccess({ ai_analyses_remaining: 0, tier: "free" });
      setStep("upgrade");
    } finally {
      setLoadingAccess(false);
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validTypes = ["video/mp4", "video/quicktime", "video/webm", "video/x-msvideo"];
    if (!validTypes.includes(file.type)) {
      toast.error("Invalid file type. Please upload MP4, MOV, WebM, or AVI.");
      return;
    }

    // Validate file size (50MB)
    if (file.size > 50 * 1024 * 1024) {
      toast.error("File too large. Maximum size is 50MB.");
      return;
    }

    setVideoFile(file);
    setVideoPreview(URL.createObjectURL(file));
    setError(null);
  };

  const handleUpload = async () => {
    if (!videoFile) {
      toast.error("Please select a video first");
      return;
    }

    // Check if user can use AI analysis
    try {
      const token = localStorage.getItem("token");
      const accessCheck = await axios.post(`${API}/api/subscription/use-ai-analysis`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (accessCheck.data.remaining >= 0) {
        toast.info(`${accessCheck.data.remaining} analyses remaining this month`);
      }
    } catch (err) {
      if (err.response?.status === 403) {
        setError(err.response?.data?.detail || "Upgrade required for AI Drill Analyzer");
        setStep("upgrade");
        return;
      }
    }

    setStep("processing");
    setUploadProgress(0);

    const formData = new FormData();
    formData.append("video", videoFile);
    formData.append("drill_context", drillName);
    formData.append("focus_areas", focusAreas);

    try {
      const token = localStorage.getItem("token");
      
      // Upload video
      const response = await axios.post(`${API}/api/drill-analyzer/upload`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Authorization: `Bearer ${token}`
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(progress);
        }
      });

      setAnalysisId(response.data.id);
      toast.success("Video uploaded! AI is analyzing...");

      // Start polling for results
      pollForResults(response.data.id);

    } catch (err) {
      console.error("Upload error:", err);
      setError(err.response?.data?.detail || "Failed to upload video");
      setStep("upload");
      toast.error("Upload failed. Please try again.");
    }
  };

  const pollForResults = (id) => {
    let attempts = 0;
    const maxAttempts = 60; // 2 minutes max

    pollIntervalRef.current = setInterval(async () => {
      attempts++;
      
      try {
        const token = localStorage.getItem("token");
        const response = await axios.get(`${API}/api/drill-analyzer/status/${id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });

        if (response.data.status === "completed" || response.data.status === "completed_with_raw") {
          clearInterval(pollIntervalRef.current);
          setResults(response.data);
          setStep("results");
          toast.success("Analysis complete!");
        } else if (response.data.status === "error") {
          clearInterval(pollIntervalRef.current);
          setError(response.data.error);
          setStep("upload");
          toast.error("Analysis failed. Please try again.");
        }

        // Update progress indicator
        setUploadProgress(Math.min(100, 50 + (attempts / maxAttempts) * 50));

      } catch (err) {
        console.error("Polling error:", err);
      }

      if (attempts >= maxAttempts) {
        clearInterval(pollIntervalRef.current);
        setError("Analysis timed out. Please try again with a shorter video.");
        setStep("upload");
      }
    }, 2000);
  };

  const resetAnalyzer = () => {
    setStep("upload");
    setVideoFile(null);
    setVideoPreview(null);
    setResults(null);
    setError(null);
    setUploadProgress(0);
    setAnalysisId(null);
    if (pollIntervalRef.current) {
      clearInterval(pollIntervalRef.current);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 8) return "text-green-400 bg-green-500/20";
    if (score >= 6) return "text-yellow-400 bg-yellow-500/20";
    if (score >= 4) return "text-orange-400 bg-orange-500/20";
    return "text-red-400 bg-red-500/20";
  };

  const getOverallGrade = (score) => {
    if (score >= 9) return { grade: "A+", color: "text-green-400" };
    if (score >= 8) return { grade: "A", color: "text-green-400" };
    if (score >= 7) return { grade: "B+", color: "text-blue-400" };
    if (score >= 6) return { grade: "B", color: "text-blue-400" };
    if (score >= 5) return { grade: "C", color: "text-yellow-400" };
    return { grade: "D", color: "text-orange-400" };
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        onClick={(e) => e.target === e.currentTarget && onClose()}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="w-full max-w-2xl max-h-[90vh] overflow-y-auto"
        >
          <Card className="bg-zinc-900 border-zinc-700">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
                  <Sparkles className="w-6 h-6 text-white" />
                </div>
                <div>
                  <CardTitle className="text-xl font-heading text-white">AI Drill Analyzer</CardTitle>
                  <p className="text-sm text-muted-foreground">Upload a video for form analysis</p>
                </div>
              </div>
              <Button variant="ghost" size="icon" onClick={onClose}>
                <X className="w-5 h-5" />
              </Button>
            </CardHeader>

            <CardContent className="space-y-4">
              {/* Upload Step */}
              {step === "upload" && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-4"
                >
                  {/* Video Upload Area */}
                  <div
                    onClick={() => fileInputRef.current?.click()}
                    className={`relative border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-all ${
                      videoPreview 
                        ? "border-purple-500 bg-purple-500/10" 
                        : "border-zinc-600 hover:border-purple-500 hover:bg-purple-500/5"
                    }`}
                  >
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="video/mp4,video/quicktime,video/webm,video/x-msvideo"
                      onChange={handleFileSelect}
                      className="hidden"
                    />

                    {videoPreview ? (
                      <div className="space-y-3">
                        <video
                          src={videoPreview}
                          className="w-full max-h-48 rounded-lg mx-auto object-contain bg-black"
                          controls
                        />
                        <p className="text-sm text-purple-400 font-medium">{videoFile?.name}</p>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={(e) => {
                            e.stopPropagation();
                            setVideoFile(null);
                            setVideoPreview(null);
                          }}
                        >
                          <RotateCcw className="w-4 h-4 mr-2" />
                          Choose Different Video
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        <div className="w-16 h-16 mx-auto rounded-full bg-purple-500/20 flex items-center justify-center">
                          <Upload className="w-8 h-8 text-purple-400" />
                        </div>
                        <div>
                          <p className="text-white font-medium">Drop your video here or click to browse</p>
                          <p className="text-sm text-muted-foreground mt-1">
                            MP4, MOV, WebM, AVI • Max 50MB
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Drill Context */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-white">What movement is this?</label>
                    <Input
                      placeholder="e.g., Defensive Slide, Box Jump, Shooting Form..."
                      value={drillName}
                      onChange={(e) => setDrillName(e.target.value)}
                      className="bg-zinc-800 border-zinc-700"
                    />
                  </div>

                  {/* Focus Areas */}
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-white">Any specific areas to focus on? (optional)</label>
                    <Textarea
                      placeholder="e.g., knee position, arm angle, core stability..."
                      value={focusAreas}
                      onChange={(e) => setFocusAreas(e.target.value)}
                      className="bg-zinc-800 border-zinc-700 min-h-[80px]"
                    />
                  </div>

                  {/* Error Display */}
                  {error && (
                    <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg">
                      <p className="text-red-400 text-sm flex items-center gap-2">
                        <AlertTriangle className="w-4 h-4" />
                        {error}
                      </p>
                    </div>
                  )}

                  {/* Analyze Button */}
                  <Button
                    onClick={handleUpload}
                    disabled={!videoFile}
                    className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500"
                    size="lg"
                    data-testid="analyze-video-btn"
                  >
                    <Sparkles className="w-5 h-5 mr-2" />
                    Analyze My Form
                  </Button>

                  {/* Tips */}
                  <div className="p-3 bg-zinc-800/50 rounded-lg">
                    <p className="text-xs text-muted-foreground">
                      <strong className="text-white">Tips for best results:</strong> Use good lighting, 
                      record from the side or front, keep full body in frame, 10-30 seconds is ideal.
                    </p>
                  </div>

                  {/* Usage Info */}
                  {featureAccess && featureAccess.ai_analyses_limit !== -1 && featureAccess.ai_analyses_remaining > 0 && (
                    <div className="p-2 bg-blue-500/10 border border-blue-500/30 rounded-lg text-center">
                      <p className="text-xs text-blue-400">
                        {featureAccess.ai_analyses_remaining} of {featureAccess.ai_analyses_limit} analyses remaining this month
                      </p>
                    </div>
                  )}
                  {featureAccess && featureAccess.ai_analyses_limit === -1 && (
                    <div className="p-2 bg-amber-500/10 border border-amber-500/30 rounded-lg text-center">
                      <p className="text-xs text-amber-400 flex items-center justify-center gap-1">
                        <Crown className="w-3 h-3" />
                        Elite: Unlimited analyses
                      </p>
                    </div>
                  )}
                </motion.div>
              )}

              {/* Upgrade Step */}
              {step === "upgrade" && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="py-8 text-center space-y-6"
                >
                  <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center">
                    <Crown className="w-10 h-10 text-white" />
                  </div>

                  <div>
                    <h3 className="text-xl font-bold text-white mb-2">Upgrade Required</h3>
                    <p className="text-muted-foreground">
                      {featureAccess?.tier === "free" || featureAccess?.tier === "basic" 
                        ? "AI Drill Analyzer is available with Pro and Elite subscriptions."
                        : "You've used all your analyses this month."}
                    </p>
                  </div>

                  <div className="grid gap-3 max-w-sm mx-auto">
                    <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-lg text-left">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-bold text-purple-400">Pro</span>
                        <Badge className="bg-purple-500/20 text-purple-400">$14.99/mo</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">3 AI analyses per month</p>
                    </div>
                    
                    <div className="p-4 bg-amber-500/10 border border-amber-500/30 rounded-lg text-left">
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-bold text-amber-400 flex items-center gap-1">
                          <Crown className="w-4 h-4" /> Elite
                        </span>
                        <Badge className="bg-amber-500/20 text-amber-400">$24.99/mo</Badge>
                      </div>
                      <p className="text-sm text-muted-foreground">Unlimited AI analyses</p>
                    </div>
                  </div>

                  <div className="flex gap-3 justify-center">
                    <Button
                      onClick={() => window.location.href = "/subscription"}
                      className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500"
                    >
                      <Crown className="w-4 h-4 mr-2" />
                      View Plans
                    </Button>
                    <Button variant="outline" onClick={onClose}>
                      Maybe Later
                    </Button>
                  </div>
                </motion.div>
              )}

              {/* Processing Step */}
              {step === "processing" && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="py-8 text-center space-y-6"
                >
                  <div className="relative w-24 h-24 mx-auto">
                    <div className="absolute inset-0 bg-purple-500/20 rounded-full animate-ping" />
                    <div className="absolute inset-2 bg-purple-500/30 rounded-full animate-pulse" />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <Loader2 className="w-10 h-10 text-purple-400 animate-spin" />
                    </div>
                  </div>

                  <div>
                    <h3 className="text-lg font-bold text-white mb-2">AI is Analyzing Your Movement</h3>
                    <p className="text-muted-foreground text-sm">
                      Examining body positioning, joint angles, and movement patterns...
                    </p>
                  </div>

                  <div className="max-w-xs mx-auto">
                    <Progress value={uploadProgress} className="h-2" />
                    <p className="text-xs text-muted-foreground mt-2">
                      {uploadProgress < 50 ? "Uploading video..." : "Processing analysis..."}
                    </p>
                  </div>

                  <div className="grid grid-cols-4 gap-2 max-w-xs mx-auto text-xs">
                    <div className={`p-2 rounded ${uploadProgress > 25 ? 'bg-purple-500/30' : 'bg-zinc-800'}`}>
                      <Eye className="w-4 h-4 mx-auto mb-1" />
                      <span>Detecting</span>
                    </div>
                    <div className={`p-2 rounded ${uploadProgress > 50 ? 'bg-purple-500/30' : 'bg-zinc-800'}`}>
                      <Target className="w-4 h-4 mx-auto mb-1" />
                      <span>Tracking</span>
                    </div>
                    <div className={`p-2 rounded ${uploadProgress > 75 ? 'bg-purple-500/30' : 'bg-zinc-800'}`}>
                      <TrendingUp className="w-4 h-4 mx-auto mb-1" />
                      <span>Analyzing</span>
                    </div>
                    <div className={`p-2 rounded ${uploadProgress >= 100 ? 'bg-purple-500/30' : 'bg-zinc-800'}`}>
                      <CheckCircle className="w-4 h-4 mx-auto mb-1" />
                      <span>Complete</span>
                    </div>
                  </div>
                </motion.div>
              )}

              {/* Results Step */}
              {step === "results" && results && (
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="space-y-4"
                >
                  {/* Overall Score */}
                  <div className="text-center p-6 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-xl border border-purple-500/30">
                    <p className="text-sm text-muted-foreground mb-2">Overall Score</p>
                    <div className="flex items-center justify-center gap-4">
                      <span className={`text-6xl font-bold ${getOverallGrade(results.overall_score || 0).color}`}>
                        {results.overall_score || "N/A"}
                      </span>
                      <span className="text-4xl text-white">/10</span>
                      <Badge className={`text-2xl px-4 py-2 ${getScoreColor(results.overall_score || 0)}`}>
                        {getOverallGrade(results.overall_score || 0).grade}
                      </Badge>
                    </div>
                    {results.movement_type && (
                      <p className="mt-2 text-purple-300">
                        Movement: <strong>{results.movement_type}</strong>
                        {results.sport_context && (
                          <Badge className="ml-2 bg-blue-500/20 text-blue-400">{results.sport_context}</Badge>
                        )}
                      </p>
                    )}
                    {results.score_justification && (
                      <p className="mt-2 text-sm text-muted-foreground">{results.score_justification}</p>
                    )}
                  </div>

                  {/* Detailed Scores */}
                  {results.form_scores && (
                    <div className="space-y-2">
                      <button
                        onClick={() => setShowDetailedScores(!showDetailedScores)}
                        className="flex items-center gap-2 text-sm font-medium text-white"
                      >
                        {showDetailedScores ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                        Detailed Form Scores
                      </button>
                      
                      <AnimatePresence>
                        {showDetailedScores && (
                          <motion.div
                            initial={{ height: 0, opacity: 0 }}
                            animate={{ height: "auto", opacity: 1 }}
                            exit={{ height: 0, opacity: 0 }}
                            className="grid grid-cols-2 gap-3 overflow-hidden"
                          >
                            {Object.entries(results.form_scores).map(([key, score]) => (
                              <div key={key} className="p-3 bg-zinc-800/50 rounded-lg">
                                <div className="flex justify-between items-center mb-1">
                                  <span className="text-xs text-muted-foreground capitalize">
                                    {key.replace(/_/g, ' ')}
                                  </span>
                                  <Badge className={getScoreColor(score)}>{score}/10</Badge>
                                </div>
                                <Progress value={score * 10} className="h-1.5" />
                              </div>
                            ))}
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </div>
                  )}

                  {/* Safety Warnings */}
                  {results.safety_warnings && results.safety_warnings.length > 0 && (
                    <div className="p-3 bg-red-500/20 border border-red-500/30 rounded-lg">
                      <p className="text-sm font-medium text-red-400 flex items-center gap-2 mb-2">
                        <AlertTriangle className="w-4 h-4" />
                        Safety Concerns
                      </p>
                      <ul className="space-y-1">
                        {results.safety_warnings.map((warning, i) => (
                          <li key={i} className="text-sm text-red-300">• {warning}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Corrections */}
                  {results.corrections && results.corrections.length > 0 && (
                    <div className="p-4 bg-orange-500/10 border border-orange-500/30 rounded-lg">
                      <p className="text-sm font-medium text-orange-400 flex items-center gap-2 mb-3">
                        <Target className="w-4 h-4" />
                        Areas to Improve
                      </p>
                      <ul className="space-y-2">
                        {results.corrections.map((correction, i) => (
                          <li key={i} className="text-sm text-white flex items-start gap-2">
                            <span className="text-orange-400">→</span>
                            {correction}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Positives */}
                  {results.positives && results.positives.length > 0 && (
                    <div className="p-4 bg-green-500/10 border border-green-500/30 rounded-lg">
                      <p className="text-sm font-medium text-green-400 flex items-center gap-2 mb-3">
                        <CheckCircle className="w-4 h-4" />
                        What You're Doing Well
                      </p>
                      <ul className="space-y-2">
                        {results.positives.map((positive, i) => (
                          <li key={i} className="text-sm text-white flex items-start gap-2">
                            <span className="text-green-400">✓</span>
                            {positive}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Drill Recommendations */}
                  {results.drill_recommendations && results.drill_recommendations.length > 0 && (
                    <div className="p-4 bg-blue-500/10 border border-blue-500/30 rounded-lg">
                      <p className="text-sm font-medium text-blue-400 flex items-center gap-2 mb-3">
                        <Dumbbell className="w-4 h-4" />
                        Recommended Drills
                      </p>
                      <div className="space-y-3">
                        {results.drill_recommendations.map((drill, i) => (
                          <div key={i} className="p-3 bg-zinc-800/50 rounded-lg">
                            <p className="font-medium text-white">{drill.name}</p>
                            <p className="text-sm text-muted-foreground">{drill.description}</p>
                            {drill.reps && (
                              <Badge className="mt-2 bg-blue-500/20 text-blue-400">{drill.reps}</Badge>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Raw Analysis (fallback) */}
                  {results.status === "completed_with_raw" && results.raw_analysis && (
                    <div className="p-4 bg-zinc-800/50 rounded-lg">
                      <p className="text-sm font-medium text-white mb-2">AI Analysis</p>
                      <p className="text-sm text-muted-foreground whitespace-pre-wrap">
                        {results.raw_analysis}
                      </p>
                    </div>
                  )}

                  {/* Action Buttons */}
                  <div className="flex gap-3 pt-4">
                    <Button
                      onClick={resetAnalyzer}
                      variant="outline"
                      className="flex-1"
                    >
                      <Video className="w-4 h-4 mr-2" />
                      Analyze Another Video
                    </Button>
                    <Button
                      onClick={onClose}
                      className="flex-1 bg-gradient-to-r from-purple-600 to-pink-600"
                    >
                      Done
                    </Button>
                  </div>
                </motion.div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default DrillAnalyzer;
